import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { ActivitiesPipesModule } from '@wilson/activities/pipes';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  AccurateActivityTimeDetails,
  ItemViewModel,
  Label,
  Project,
} from '@wilson/interfaces';
import {
  GanttEndingPixelPipe,
  GanttStartingPixelPipe,
  SelectedItemOutput,
  ShiftCardComponent,
  ShiftCardTooltipStateService,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  GanttShiftEndTimePipe,
  GanttShiftStartTimePipe,
  ShiftTimelinePipesModule,
} from '@wilson/shift-timeline/pipes';
import { DragAndDropModeService } from '@wilson/shift-timeline/services';
import { Observable, of } from 'rxjs';
import { LoadingShiftCardComponent } from '../loading-shift-card/loading-shift-card.component';
import { TimelineAutoSizeShiftWithActivitiesCardComponent } from '../timeline-auto-size-shift-with-activities-card/timeline-auto-size-shift-with-activities-card.component';
import { LabelsState } from '@wilson/labels';
import { ProjectsState } from '@wilson/projects/state';
import { Store } from '@ngxs/store';

@Component({
  selector: 'wilson-timeline-auto-size-user-shifts',
  providers: [
    ShiftCardTooltipStateService,
    GanttShiftStartTimePipe,
    GanttShiftEndTimePipe,
    GanttStartingPixelPipe,
    GanttEndingPixelPipe,
  ],
  templateUrl: './timeline-auto-size-user-shifts.component.html',
  styleUrls: ['./timeline-auto-size-user-shifts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TimelineAutoSizeShiftWithActivitiesCardComponent,
    ShiftTimelinePipesModule,
    LoadingShiftCardComponent,
    ShiftCardComponent,
    FeatureFlagsModule,
    ActivitiesPipesModule,
  ],
})
export class TimelineAutoSizeUserShiftsComponent implements OnInit, OnChanges {
  readonly dragAndDropModeService = inject(DragAndDropModeService);
  readonly shiftCardTooltipStateService = inject(ShiftCardTooltipStateService);
  private readonly ganttActivityStartingPixelPipe = inject(
    GanttStartingPixelPipe,
  );
  private readonly ganttActivityEndingPixelPipe = inject(GanttEndingPixelPipe);
  private readonly ganttShiftStartTimePipe = inject(GanttShiftStartTimePipe);
  private readonly ganttShiftEndTimePipe = inject(GanttShiftEndTimePipe);
  private readonly store = inject(Store);

  @Input() shiftView!: ItemViewModel;
  @Input() userId!: string;
  @Input() showShiftValidation = false;
  @Input() showActivities = false;
  @Input() draggingItemId!: string | null;
  @Input() updatingShiftIds!: string[] | null;
  @Input() isCursorGrab = false;
  @Input() isActivityDraggable = false;
  @Input() wrapperEle!: HTMLElement | null;

  @Output() selectShiftEvent = new EventEmitter<SelectedItemOutput>();
  @Output() selectActivityEvent = new EventEmitter<SelectedItemOutput>();

  leftPixelPosition$!: Observable<number>;
  rightPixelPosition$!: Observable<number>;
  shiftStartDetail!: AccurateActivityTimeDetails;
  shiftEndDetail!: AccurateActivityTimeDetails;
  protected project$!: Observable<Project | null | undefined>;
  protected label$!: Observable<Label | null | undefined>;
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['shiftView']) {
      this.shiftCardTooltipStateService.initState(
        this.userId,
        this.shiftView.shift,
      );

      this.project$ = this.shiftView.shift.projectId
        ? this.store.select(
            ProjectsState.projectById(this.shiftView.shift.projectId),
          )
        : of(null);

      this.label$ = this.shiftView.shift.labels?.length
        ? this.store.select(
            LabelsState.getLabelById(this.shiftView.shift.labels[0].id),
          )
        : of(null);
    }
  }
  ngOnInit(): void {
    this.shiftStartDetail = this.ganttShiftStartTimePipe.transform(
      this.shiftView.shift.activities,
    );

    this.shiftEndDetail = this.ganttShiftEndTimePipe.transform(
      this.shiftView.shift.activities,
    );

    this.leftPixelPosition$ = this.ganttActivityStartingPixelPipe.transform(
      this.shiftStartDetail.date,
    );

    this.rightPixelPosition$ = this.ganttActivityEndingPixelPipe.transform([
      this.shiftStartDetail.date,
      this.shiftEndDetail.date,
    ]);
  }

  selectedShift(event: SelectedItemOutput): void {
    this.selectShiftEvent.next(event);
  }
}
